// Карточка поста из «Добавленных»: кто добавил, «Обновить цифры», «Убрать из
// Добавленных». Пока автор не отслеживается, ×N нет: вместо него доля
// просмотров от подписчиков и «Отслеживать автора».

import { useQueryClient } from '@tanstack/react-query'
import { Hand, ListMinus, Radar, RefreshCw } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { isApiError } from '../../api/client'
import { keys, useRemoveManual } from '../../api/queries'
import { isActive, startErrorText, useJob, useStartRefresh, useToolsInfo } from '../../api/tools'
import type { PostDetail } from '../../api/types'
import { feedMeasurement, neighbours, useFeedOrder, type OverlayState } from '../../lib/feedNav'
import { postHref, postMeasurement, primaryViews, refreshResultMessage } from '../../lib/measurement'
import { followerShare, formatDateTime, formatPct, sourceInput } from '../../lib/format'
import { Button } from '../Button'
import { Spinner } from '../Spinner'
import { toast } from '../Toast'
import { AddSourceDialog } from '../sources/AddSourceDialog'
import { useBack } from '../../lib/useBack'
import { useViewState } from '../../lib/navigation'

const usd = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 3 })

/** ×N нет: автор не собирается. После «Отслеживать» плашка уходит, ×N посчитается с прочёсом. */
export function isUntracked(post: PostDetail): boolean {
  return Boolean(post.manual) && post.xn === null && !post.author.tracked
}

export function AddedBlock({ post }: { post: PostDetail }) {
  // Окно источника — здесь, а не в плашке: плашка пропадёт, как только автор начнёт собираться.
  const [tracking, setTracking] = useViewState(`post.${post.id}.tracking`, false)
  const manual = post.manual
  if (!manual) return null
  return (
    <>
      {isUntracked(post) && <Untracked post={post} onTrack={() => setTracking(true)} />}
      <section className="flex flex-col gap-2 rounded-xl bg-surface px-3.5 py-2.5 text-sm ring-1 ring-line ring-inset">
        <p className="flex items-start gap-2">
          <Hand size={16} className="mt-0.5 shrink-0 text-muted" />
          <span className="min-w-0">
            <span className="font-medium">{manual.added_by_name ? `Добавил ${manual.added_by_name}` : 'Добавлен вручную'}</span>
            <span className="text-muted">, {formatDateTime(manual.added_at)}</span>
          </span>
        </p>
        <div className="flex flex-wrap gap-2">
          <RefreshButton post={post} />
          <RemoveButton postId={post.id} />
        </div>
      </section>
      {tracking && (
        <AddSourceDialog initialInput={sourceInput('instagram', post.author.handle)} onClose={() => setTracking(false)} />
      )}
    </>
  )
}

function Untracked({ post, onTrack }: { post: PostDetail; onTrack: () => void }) {
  const share = followerShare(primaryViews(post), post.author.followers)
  return (
    <div className="rounded-xl bg-surface px-4 py-3 ring-1 ring-line ring-inset">
      {share !== null && (
        <div className="flex flex-wrap items-baseline gap-x-2.5">
          <span className="text-4xl leading-tight font-semibold tracking-tight tabular-nums">{formatPct(share)}</span>
          <span className="text-sm font-medium">доля просмотров от подписчиков</span>
        </div>
      )}
      <p className={`text-sm font-medium ${share !== null ? 'mt-1' : ''}`}>Автор не отслеживается — ×N нет</p>
      <p className="mt-0.5 text-sm text-pretty text-muted">
        {share === null && 'Подписчиков автора узнать не удалось. '}
        Отслеживайте автора — появится ×N, а цифры будут обновляться сами.
      </p>
      <Button size="sm" className="mt-2.5" onClick={onTrack}>
        <Radar size={14} />
        Отслеживать автора
      </Button>
    </div>
  )
}

/** Цифры заново через Apify: задача воркера, ждём её и перечитываем карточку и ленту. */
function RefreshButton({ post }: { post: PostDetail }) {
  const qc = useQueryClient()
  const info = useToolsInfo()
  const start = useStartRefresh(post.id)
  const [jobId, setJobId] = useViewState<string | null>(`post.${post.id}.refresh-job`, null)
  const job = useJob(jobId)
  const handled = useRef<string | null>(null)

  const data = job.data
  useEffect(() => {
    if (!data || isActive(data) || handled.current === data.id) return
    handled.current = data.id
    setJobId(null)
    if (data.status === 'done') {
      void qc.invalidateQueries({ queryKey: keys.post(post.id) })
      void qc.invalidateQueries({ queryKey: keys.feedAll })
      toast(refreshResultMessage(data.result))
    } else {
      toast(data.error ?? 'Не получилось обновить цифры', { error: true, ms: 4000 })
    }
  }, [data, qc, post.id])

  // Задачу уже убрали (старше суток) — кнопка снова доступна.
  useEffect(() => {
    if (jobId && isApiError(job.error, 404)) setJobId(null)
  }, [jobId, job.error])

  const busy = start.isPending || jobId !== null
  const price = post.metric_view !== 'meta' && info.data ? ` ≈$${usd.format(info.data.analyze_price_usd)}` : ''
  const run = () =>
    start.mutate(undefined, {
      onSuccess: (created) => setJobId(created.id),
      onError: (err) => toast(startErrorText(isApiError(err) ? err.detail : null), { error: true, ms: 4000 }),
    })

  return (
    <Button size="sm" onClick={run} disabled={busy}>
      {busy ? <Spinner size={14} /> : <RefreshCw size={14} />}
      {busy ? 'Обновляю цифры…' : `Обновить цифры${price}`}
    </Button>
  )
}

/**
 * Сам пост остаётся в базе. Карточка открыта над «Добавленными» — листаем к
 * соседнему, как «Убрать из группы» в избранном: этот из сетки пропадёт.
 */
function RemoveButton({ postId }: { postId: number }) {
  const qc = useQueryClient()
  const remove = useRemoveManual()
  const navigate = useNavigate()
  const back = useBack('/?view=added')
  const location = useLocation()
  const order = useFeedOrder()
  const background = (location.state as OverlayState | null)?.backgroundLocation
  const overAdded = new URLSearchParams(background?.search ?? '').get('view') === 'added'

  const run = () =>
    remove.mutate(postId, {
      onSuccess: () => {
        toast('Убрано из Добавленных')
        if (!overAdded) return
        const { prev, next } = neighbours(order, postId)
        const after = next ?? prev
        if (after !== null) void navigate(postHref({ id: after }, feedMeasurement(after) ?? {
          metric_view: postMeasurement(new URLSearchParams(location.search)).metric_view,
        }), { replace: true, state: location.state })
        else back()
      },
      onError: (err) => {
        if (!isApiError(err, 404)) return toast('Не получилось убрать', { error: true })
        // Убрал кто-то другой: карточка и лента перечитываются, блок пропадёт.
        toast('Уже убрали из Добавленных')
        void qc.invalidateQueries({ queryKey: keys.feedAll })
        void qc.invalidateQueries({ queryKey: keys.post(postId) })
      },
    })

  return (
    <Button size="sm" variant="ghost" onClick={run} disabled={remove.isPending}>
      {remove.isPending ? <Spinner size={14} /> : <ListMinus size={14} />}
      Убрать из Добавленных
    </Button>
  )
}
